import { useState, useEffect } from 'react';
import { useWorkspace } from '../../hooks/useWorkspace';
import { useAuth } from '../../hooks/useAuth';
import { apiClient } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Card } from '../../components/ui/Card';
import { InviteModal } from './InviteModal';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  role: string;
  domains: string[];
  joinedAt: string;
}

export function TeamList() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showInvite, setShowInvite] = useState(false);
  const [removingId, setRemovingId] = useState<string | null>(null);

  const canManage = currentWorkspace?.role === 'owner' || currentWorkspace?.role === 'admin';

  useEffect(() => {
    if (!currentWorkspace) return;
    fetchMembers();
  }, [currentWorkspace]);

  async function fetchMembers() {
    if (!currentWorkspace) return;
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await apiClient.get<TeamMember[]>(
      `/api/tenants/${currentWorkspace.tenantId}/users`
    );

    if (apiError) {
      setError(apiError.message);
    } else if (data) {
      setMembers(data);
    }
    setLoading(false);
  }

  async function removeMember(memberId: string) {
    if (!currentWorkspace) return;
    setRemovingId(memberId);

    const { error: apiError } = await apiClient.delete(
      `/api/tenants/${currentWorkspace.tenantId}/users/${memberId}`
    );

    if (apiError) {
      setError(apiError.message);
    } else {
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
    }
    setRemovingId(null);
  }

  const roleBadgeVariant = (role: string) => {
    switch (role) {
      case 'owner': return 'brand' as const;
      case 'admin': return 'warning' as const;
      case 'consultant': return 'info' as const;
      case 'steward': return 'success' as const;
      case 'viewer': return 'default' as const;
      default: return 'default' as const;
    }
  };

  function formatDate(dateStr: string) {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <svg className="w-8 h-8 animate-spin text-brand" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-display font-bold text-white">Team</h1>
          <p className="text-slate-400 text-sm mt-1">
            {members.length} member{members.length !== 1 ? 's' : ''}
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setShowInvite(true)}>
            Invite Member
          </Button>
        )}
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Team member list */}
      {members.length === 0 ? (
        <Card>
          <div className="text-center py-8">
            <p className="text-slate-400 text-sm">No team members yet.</p>
            {canManage && (
              <Button
                variant="secondary"
                size="sm"
                className="mt-4"
                onClick={() => setShowInvite(true)}
              >
                Invite Your First Member
              </Button>
            )}
          </div>
        </Card>
      ) : (
        <div className="bg-surface-mid rounded-xl border border-border overflow-hidden">
          {/* Table header */}
          <div className="hidden sm:grid grid-cols-12 gap-4 px-6 py-3 border-b border-border text-xs text-slate-500 uppercase tracking-wider">
            <div className="col-span-3">Name</div>
            <div className="col-span-3">Email</div>
            <div className="col-span-2">Role</div>
            <div className="col-span-2">Joined</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {/* Table rows */}
          {members.map((member) => {
            const isCurrentUser = user?.email === member.email;
            const isOwner = member.role === 'owner';

            return (
              <div
                key={member.id}
                className="grid grid-cols-1 sm:grid-cols-12 gap-2 sm:gap-4 px-6 py-4 border-b border-border last:border-b-0 items-center"
              >
                <div className="sm:col-span-3">
                  <p className="text-sm font-medium text-white">{member.name}</p>
                  {isCurrentUser && (
                    <span className="text-xs text-slate-500">(you)</span>
                  )}
                </div>
                <div className="sm:col-span-3">
                  <p className="text-sm text-slate-400 truncate">{member.email}</p>
                </div>
                <div className="sm:col-span-2">
                  <Badge variant={roleBadgeVariant(member.role)}>
                    {member.role}
                  </Badge>
                </div>
                <div className="sm:col-span-2">
                  <p className="text-sm text-slate-500">{formatDate(member.joinedAt)}</p>
                </div>
                <div className="sm:col-span-2 text-right">
                  {canManage && !isCurrentUser && !isOwner && (
                    <Button
                      variant="danger"
                      size="sm"
                      loading={removingId === member.id}
                      onClick={() => removeMember(member.id)}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Invite modal */}
      <InviteModal
        isOpen={showInvite}
        onClose={() => setShowInvite(false)}
        onInvited={fetchMembers}
      />
    </div>
  );
}
